import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from '@mui/material';
import {
  PlayArrow,
  Stop,
  Add,
  Edit,
  Delete,
  Timer,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { useAppSelector } from '../../hooks/useAppSelector';
import {
  startTimer,
  stopTimer,
  createTimeLog,
  fetchTaskTimeLogs,
  updateTimeLog,
  deleteTimeLog,
} from '../../store/timeTrackingSlice';
import { TimeLog } from '../../entities/timeTracking/model/types';
import './TimeTracker.scss';

interface TimeTrackerProps {
  taskId?: string;
  taskTitle?: string;
  userId: string;
  userName: string;
}

const TimeTracker: React.FC<TimeTrackerProps> = ({ taskId, taskTitle, userId, userName }) => {
  const dispatch = useAppDispatch();
  const { logs, activeTimer } = useAppSelector((state) => state.timeTracking);

  const [openDialog, setOpenDialog] = useState(false);
  const [editingLog, setEditingLog] = useState<TimeLog | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [formData, setFormData] = useState({
    hours: 0,
    minutes: 0,
    notes: '',
    date: new Date().toISOString().split('T')[0],
  });

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (activeTimer && !activeTimer.isPaused) {
      interval = setInterval(() => {
        const start = new Date(activeTimer.startTime).getTime();
        const now = new Date().getTime();
        setElapsedTime(Math.floor((now - start) / 1000));
      }, 1000);
    } else if (!activeTimer) {
      setElapsedTime(0);
    }
    return () => clearInterval(interval);
  }, [activeTimer]);

  // Fetch time logs for task
  useEffect(() => {
    if (taskId) {
      dispatch(fetchTaskTimeLogs(taskId));
    }
  }, [dispatch, taskId]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const handleStartTimer = () => {
    if (taskId && taskTitle) {
      dispatch(startTimer({
        taskId,
        taskName: taskTitle,
        notes: formData.notes
      }));
    }
  };

  const handleStopTimer = async () => {
    if (activeTimer) {
      dispatch(stopTimer({ notes: formData.notes }));
    }
  };

  const handleOpenDialog = (log?: TimeLog) => {
    // Prevent opening dialog if timer is active
    if (activeTimer && !log) {
      alert('Please stop the active timer before logging time manually');
      return;
    }

    if (log) {
      setEditingLog(log);
      setFormData({
        hours: log.hours,
        minutes: log.minutes,
        notes: log.notes || '',
        date: new Date(log.date).toISOString().split('T')[0],
      });
    } else {
      setEditingLog(null);
      setFormData({
        hours: 0,
        minutes: 0,
        notes: '',
        date: new Date().toISOString().split('T')[0],
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLog(null);
  };

  const handleSubmit = async () => {
    if (!taskId) return;

    if (editingLog) {
      // Update existing log
      await dispatch(
        updateTimeLog({
          logId: editingLog.id,
          updates: {
            hours: formData.hours,
            minutes: formData.minutes,
            notes: formData.notes,
            date: new Date(formData.date).toISOString(),
          },
        })
      );
    } else {
      // Create new log
      await dispatch(
        createTimeLog({
          taskId,
          hours: formData.hours,
          minutes: formData.minutes,
          notes: formData.notes,
          date: new Date(formData.date).toISOString(),
        })
      );
    }

    handleCloseDialog();
  };

  const handleDelete = async (logId: string) => {
    if (window.confirm('Are you sure you want to delete this time log?')) {
      await dispatch(deleteTimeLog(logId));
    }
  };

  const getTotalHours = () => {
    return logs.reduce((sum: number, log: TimeLog) => sum + log.hours + (log.minutes / 60), 0).toFixed(2);
  };

  return (
    <Box className="time-tracker">
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center" gap={1}>
              <Timer />
              <Typography variant="h6">Time Tracking</Typography>
              {activeTimer && (
                <Typography variant="body2" color="text.secondary">
                  {activeTimer.taskName}
                </Typography>
              )}
            </Box>
            {activeTimer ? (
              <Box display="flex" alignItems="center" gap={2}>
                <Typography variant="h5" color="primary">
                  {formatTime(elapsedTime)}
                </Typography>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={handleStopTimer}
                >
                  Stop Timer
                </Button>
              </Box>
            ) : (
              <Box display="flex" gap={1}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PlayArrow />}
                  onClick={handleStartTimer}
                  disabled={!taskId}
                >
                  Start Timer
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => handleOpenDialog()}
                  disabled={!taskId || !!activeTimer}
                  title={activeTimer ? 'Stop the timer first to log time manually' : 'Log time manually'}
                >
                  Log Time
                </Button>
              </Box>
            )}
          </Box>

          {/* Total Hours Display */}
          <Box mb={2}>
            <Typography variant="body2" color="text.secondary">
              Total Logged: <strong>{getTotalHours()}h</strong>
            </Typography>
          </Box>

          {/* Time Logs Table */}
          {logs.length > 0 && (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Notes</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {logs.map((log: TimeLog) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        {new Date(log.date).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={`${log.hours}h ${log.minutes}m`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{log.notes || '-'}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(log)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(log.id)}
                          color="error"
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {logs.length === 0 && !activeTimer && (
            <Box textAlign="center" py={3}>
              <Typography variant="body2" color="text.secondary">
                No time logs yet. Start a timer or log time manually.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Log Time Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLog ? 'Edit Time Log' : 'Log Time'}
        </DialogTitle>
        <DialogContent>
          {editingLog?.isActive && (
            <Box sx={{ mb: 2, mt: 1, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="body2" color="warning.dark">
                ⚠️ This log is from an active timer. Time and date fields are read-only.
              </Typography>
            </Box>
          )}
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Box display="flex" gap={2}>
              <TextField
                label="Hours"
                type="number"
                value={formData.hours}
                onChange={(e) =>
                  setFormData({ ...formData, hours: Math.max(0, parseInt(e.target.value) || 0) })
                }
                fullWidth
                inputProps={{ min: 0, max: 24 }}
                disabled={editingLog?.isActive}
              />
              <TextField
                label="Minutes"
                type="number"
                value={formData.minutes}
                onChange={(e) =>
                  setFormData({ ...formData, minutes: Math.max(0, Math.min(59, parseInt(e.target.value) || 0)) })
                }
                fullWidth
                inputProps={{ min: 0, max: 59 }}
                disabled={editingLog?.isActive}
              />
            </Box>
            <TextField
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              fullWidth
              InputLabelProps={{ shrink: true }}
              disabled={editingLog?.isActive}
            />
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              placeholder="What did you work on?"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={formData.hours === 0 && formData.minutes === 0}
          >
            {editingLog ? 'Update' : 'Log Time'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TimeTracker;
